import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';

import { useTheme } from '@mui/system';
import moment from 'moment';

import SilverBoomReport from '../../../resources/images/logos/SilverBoom Report.svg';
import FCard from '../../ui/fairtreeComponents/card/fCard';
import FCardHeader from '../../ui/fairtreeComponents/card/fCardHeader';
import FCardTypography from '../../ui/fairtreeComponents/card/fCardTypography';
import FCardCurrencyTypography from '../../ui/fairtreeComponents/card/fCardCurrencyTypography';
import currencyHelper from '../../../helpers/currencyHelper';

const AccountSummaryCard = ({ account, accountNumber }) => {
  const theme = useTheme();

  // ✅ SAFE NORMALIZATION (CRITICAL FIX)
  const balances = useMemo(() => {
    return Array.isArray(account?.balances) ? account.balances : [];
  }, [account]);

  const currency = account?.ccy ? currencyHelper(account.ccy) : currencyHelper('ZAR');

  // 🔥 GROUP BY STRATEGY
  const groupedByStrategy = useMemo(() => {
    return balances.reduce((acc, item) => {
      const strategy = item?.strategy?.trim();

      if (!strategy) return acc;

      if (!acc[strategy]) acc[strategy] = [];
      acc[strategy].push(item);

      return acc;
    }, {});
  }, [balances]);

  // 🔥 UNGROUPED (NO STRATEGY)
  const ungroupedBalances = useMemo(() => {
    return balances.filter((b) => !b?.strategy?.trim());
  }, [balances]);

  // 🔥 TOTAL NAV (optional but useful)
  const total = useMemo(() => {
    return balances.reduce((sum, b) => sum + Number(b.closePositionValueVal || 0), 0);
  }, [balances]);

  return (
    <FCard>
      <FCardHeader
        title={
          <Box display="flex" justifyContent="space-between">
            {account?.model?.name}
          </Box>
        }
        subheader={<Typography variant="caption">{accountNumber}</Typography>}
      />

      <Box px={2} pb={2}>
        {/* 🔹 DATE */}
        <Typography variant="body2" textAlign="right" sx={{ color: theme.palette.accent?.main }}>
          {balances.length > 0 && moment(account?.priceDate).format('DD MMMM YYYY')} Closing
        </Typography>

        {/* 🔹 STRATEGY GROUPS */}
        {Object.entries(groupedByStrategy).map(([strategyName, items]) => {
          const strategyTotal = items.reduce(
            (sum, i) => sum + Number(i.closePositionValueVal || 0),
            0
          );

          return (
            <Accordion
              key={strategyName}
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: 'transparent',
                '&:before': { display: 'none' }
              }}>
              <AccordionSummary
                expandIcon={
                  <Box
                    component="img"
                    src={SilverBoomReport}
                    sx={{
                      width: 22,
                      height: 22,
                      transform: 'rotate(90deg)'
                    }}
                  />
                }
                sx={{ px: 0 }}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <FCardTypography sx={{ textTransform: 'capitalize' }}>
                    {strategyName}
                  </FCardTypography>

                  <FCardCurrencyTypography>
                    {currency}{' '}
                    {strategyTotal.toLocaleString('en-ZA', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </FCardCurrencyTypography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pl: 2 }}>
                {items.map((row, idx) => (
                  <Box key={idx} display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
                    <FCardTypography>{row.instrument}</FCardTypography>

                    <FCardCurrencyTypography>
                      {currency}{' '}
                      {Number(row.closePositionValueVal || 0).toLocaleString('en-ZA', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </FCardCurrencyTypography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* 🔹 UNGROUPED */}
        {ungroupedBalances.map((row, idx) => (
          <Box key={idx} display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
            <FCardTypography>{row.instrument}</FCardTypography>

            <FCardCurrencyTypography>
              {currency}{' '}
              {Number(row.closePositionValueVal || 0).toLocaleString('en-ZA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </FCardCurrencyTypography>
          </Box>
        ))}
      </Box>

      {/* 🔹 FOOTER */}
      <Box px={2} pb={2}>
        <Typography variant="caption">Administered by {account?.administrator}</Typography>
      </Box>
    </FCard>
  );
};

export default AccountSummaryCard;

AccountSummaryCard.propTypes = {
  account: PropTypes.object.isRequired,
  accountNumber: PropTypes.string.isRequired
};
